import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CustomForms = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formDetail, setFormDetail] = useState(null);
  const [formResponse, setFormResponse] = useState({});
  const [responsesList, setResponsesList] = useState([]);
  const [showResponses, setShowResponses] = useState(false);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch forms list on component mount
  useEffect(() => {
    fetchFormsList();
  }, []);

  // Fetch list of available forms from /form/list
  const fetchFormsList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${api.BASE_URL}/form/list`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Forms list received:', result);
        
        if (!result.error && result.data && Array.isArray(result.data)) {
          setForms(result.data);
        } else {
          setForms([]);
          if (result.error) {
            setError(result.message || 'Failed to fetch forms list');
          }
        }
      } else {
        setError('Failed to fetch forms list');
        setForms([]);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      setError('An error occurred while fetching forms');
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch form details (fields structure) from /form/detail/:id
  const fetchFormDetail = async (formId) => {
    setLoading(true);
    setSubmitSuccess(false);
    setError(null);
    try {
      const response = await fetch(`${api.BASE_URL}/form/detail/${formId}`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Form detail received:', result);
        
        if (!result.error && result.data) {
          setFormDetail(result.data);
          setSelectedForm(formId);
          
          // Initialize form response with empty values based on fields
          const initialResponse = {};
          if (result.data.fields && Array.isArray(result.data.fields)) {
            result.data.fields.forEach(field => {
              initialResponse[field.label || field.name] = field.type === 'number' ? '' : '';
            });
          }
          setFormResponse(initialResponse);
        } else {
          setError(result.message || 'Failed to fetch form details');
        }
      } else {
        setError('Failed to fetch form details');
      }
    } catch (error) {
      console.error('Error fetching form details:', error);
      setError('An error occurred while fetching form details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch responses list from /form/:id/response
  const fetchResponsesList = async (formId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${api.BASE_URL}/form/${formId}/response`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Responses received:', result);
        
        if (!result.error && result.data) {
          setResponsesList(Array.isArray(result.data) ? result.data : [result.data]);
          setShowResponses(true);
        } else {
          setError(result.message || 'Failed to fetch responses');
        }
      } else {
        setError('Failed to fetch responses');
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      setError('An error occurred while fetching responses');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (fieldLabel, value, fieldType) => {
    setFormResponse(prev => ({
      ...prev,
      [fieldLabel]: fieldType === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  // Submit form response to POST /form/:id/response
  const submitFormResponse = async () => {
    if (!selectedForm) return;
    
    // Validate required fields
    if (formDetail?.fields) {
      for (const field of formDetail.fields) {
        const fieldLabel = field.label || field.name;
        if (field.required && !formResponse[fieldLabel]) {
          setError(`${fieldLabel} is required`);
          return;
        }
      }
    }
    
    setSubmitLoading(true);
    setSubmitSuccess(false);
    setError(null);
    
    try {
      // Get current month-year for type (e.g., "Sep-25")
      const now = new Date();
      const monthYear = now.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      
      // Prepare payload in exact format
      const payload = {
        type: monthYear,
        response: formResponse
      };

      console.log('Submitting payload:', payload);

      const response = await fetch(`${api.BASE_URL}/form/${selectedForm}/response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        
        // Reset form after 2 seconds
        setTimeout(() => {
          setSelectedForm(null);
          setFormDetail(null);
          setFormResponse({});
          setSubmitSuccess(false);
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('An error occurred while submitting form');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Filter forms based on search
  const filteredForms = Array.isArray(forms) ? forms.filter(form => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (form.name && form.name.toLowerCase().includes(searchLower)) ||
      (form.type && form.type.toLowerCase().includes(searchLower)) ||
      (form.title && form.title.toLowerCase().includes(searchLower))
    );
  }) : [];

  // Render form field based on type
  const renderFormField = (field) => {
    const fieldLabel = field.label || field.name;
    const commonClasses = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent";
    
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={formResponse[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={field.required}
            className={commonClasses}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={formResponse[fieldLabel] === '' ? '' : formResponse[fieldLabel]}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'number')}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={field.required}
            min={field.min || 0}
            max={field.max}
            className={commonClasses}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={formResponse[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
            placeholder={field.placeholder || `Enter ${fieldLabel}`}
            required={field.required}
            rows={field.rows || 3}
            className={commonClasses}
          />
        );
      
      case 'select':
        return (
          <select
            value={formResponse[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
            required={field.required}
            className={commonClasses}
          >
            <option value="">Select {fieldLabel}</option>
            {field.options && field.options.map((option, idx) => (
              <option key={idx} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={formResponse[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
            required={field.required}
            className={commonClasses}
          />
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options && field.options.map((option, idx) => (
              <div key={idx} className="flex items-center">
                <input
                  type="radio"
                  name={fieldLabel}
                  value={option.value || option}
                  checked={formResponse[fieldLabel] === (option.value || option)}
                  onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
                  className="h-4 w-4 text-blue-600 border-gray-300"
                />
                <label className="ml-2 text-sm text-gray-700">
                  {option.label || option}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options && field.options.map((option, idx) => (
              <div key={idx} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formResponse[fieldLabel]?.includes(option.value || option) || false}
                  onChange={(e) => {
                    const value = option.value || option;
                    const currentValues = formResponse[fieldLabel] || [];
                    const newValues = e.target.checked
                      ? [...currentValues, value]
                      : currentValues.filter(v => v !== value);
                    handleInputChange(fieldLabel, newValues, 'array');
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  {option.label || option}
                </label>
              </div>
            ))}
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={formResponse[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, 'text')}
            className={commonClasses}
          />
        );
    }
  };

  if (loading && !selectedForm) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !selectedForm) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchFormsList}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Forms List View */}
      {!selectedForm && !showResponses && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-700">Available Forms</h3>
              
              {/* Search */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search forms..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredForms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredForms.map((form) => (
                <div
                  key={form.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <h4 className="text-lg font-medium text-gray-800 mb-2">
                    {form.name || form.title || `Form ${form.id}`}
                  </h4>
                  {form.description && (
                    <p className="text-sm text-gray-600 mb-3">{form.description}</p>
                  )}
                  {form.type && (
                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded mb-3">
                      {form.type}
                    </span>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchFormDetail(form.id)}
                      className="flex-1 text-blue-600 text-sm font-medium hover:text-blue-800 border border-blue-600 rounded px-3 py-2 hover:bg-blue-50"
                    >
                      Fill Form
                    </button>
                    <button
                      onClick={() => {
                        setSelectedForm(form.id);
                        fetchResponsesList(form.id);
                      }}
                      className="flex-1 text-green-600 text-sm font-medium hover:text-green-800 border border-green-600 rounded px-3 py-2 hover:bg-green-50"
                    >
                      View Responses
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              {searchTerm ? 
                `No forms found matching "${searchTerm}"` : 
                'No forms available.'
              }
            </div>
          )}
        </div>
      )}

      {/* Responses List View */}
      {showResponses && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Form Responses</h3>
              <button
                onClick={() => {
                  setShowResponses(false);
                  setResponsesList([]);
                  setSelectedForm(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Back to Forms
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : responsesList.length > 0 ? (
              <div className="space-y-4">
                {responsesList.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-blue-600">
                        {item.type || 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {item.response && Object.entries(item.response).map(([key, value]) => (
                        <div key={key} className="bg-gray-50 p-3 rounded">
                          <p className="text-xs font-medium text-gray-500 mb-1">{key}</p>
                          <p className="text-sm text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No responses found for this form.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Detail View */}
      {selectedForm && formDetail && !showResponses && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">
                  {formDetail.name || formDetail.title || `Form ${selectedForm}`}
                </h3>
                {formDetail.description && (
                  <p className="text-sm text-gray-600 mt-1">{formDetail.description}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedForm(null);
                  setFormDetail(null);
                  setFormResponse({});
                  setSubmitSuccess(false);
                  setError(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Back to Forms
              </button>
            </div>
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mx-6 mt-6 bg-green-50 border border-green-200 text-green-800 p-4 rounded-md">
              <p className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Form submitted successfully!
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-6 bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
              <p className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {error}
              </p>
            </div>
          )}

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formDetail.fields && formDetail.fields.map((field, index) => (
                    <div key={index}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label || field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {renderFormField(field)}
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex gap-3 border-t">
                  <button
                    onClick={submitFormResponse}
                    disabled={submitLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                  >
                    {submitLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      'Submit Form'
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      const initialResponse = {};
                      if (formDetail.fields) {
                        formDetail.fields.forEach(field => {
                          const fieldLabel = field.label || field.name;
                          initialResponse[fieldLabel] = field.type === 'number' ? '' : '';
                        });
                      }
                      setFormResponse(initialResponse);
                      setError(null);
                    }}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomForms;